import React, { useState, useEffect } from 'react';
import { Bug, X, RefreshCw, UploadCloud, ChevronDown, ChevronUp, Download } from 'lucide-react';

/**
 * DebugPanel - Composant réutilisable pour afficher les données localStorage et Server File
 *
 * @param {Object} props
 * @param {string} props.localStorageKey - Clé du localStorage (ex: 'cabaza_user_contacts', 'cabaza_user_places')
 * @param {string} props.serverFileName - Nom du fichier serveur (ex: 'contacts.json', 'places.json')
 * @param {Array} props.fields - Liste des champs à afficher [{key, label, color, format, width}]
 * @param {Function} props.loadFromFile - Fonction pour charger depuis le fichier serveur (peopleuuid, storageKey) => Promise<Array>
 * @param {Function} props.saveToFile - Fonction pour sauvegarder vers le fichier serveur (peopleuuid, storageKey, data) => Promise<{success, message}>
 * @param {Function} props.onDataChange - Callback quand les données changent (optionnel)
 * @param {Function} props.setMainData - Callback pour mettre à jour les données principales (optionnel, pour Full Resync)
 * @param {string} props.title - Titre du panel (ex: 'Debug Contacts', 'Debug Places')
 */
const DebugPanel = ({
  localStorageKey,
  serverFileName,
  fields,
  loadFromFile,
  saveToFile,
  onDataChange,
  setMainData,
  title = 'Debug Panel'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localStorageData, setLocalStorageData] = useState([]);
  const [serverFileData, setServerFileData] = useState([]);
  const [debugLogs, setDebugLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  // Get userId from localStorage
  const userId = parseInt(localStorage.getItem('userId'), 10);

  // Check if user is admin
  let isAdmin = false;
  try {
    const cabazaUser = JSON.parse(localStorage.getItem('cabaza_user') || '{}');
    isAdmin = cabazaUser.isAdmin === true || cabazaUser.isAdmin === 'true' || cabazaUser.isAdmin === 1 || cabazaUser.isAdmin === '1';
  } catch (e) {
    console.error('Error parsing cabaza_user:', e);
  }

  // Add log entry
  const addLog = (message, type = 'info') => {
    setDebugLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), message, type }]);
  };

  // Get user info from localStorage
  const getUserInfo = () => {
    const userJson = localStorage.getItem('cabaza_user');
    if (!userJson) return null;
    const user = JSON.parse(userJson);
    return {
      userId,
      peopleUuid: user.peopleUuid || user.peopleuuid,
      storageKey: user.storageKey
    };
  };

  // Refresh debug data
  const refreshData = async () => {
    setLoading(true);
    try {
      console.log(`=== REFRESH DEBUG DATA (${title}) ===`);

      // Refresh localStorage display
      const data = localStorage.getItem(localStorageKey);
      const allData = data ? JSON.parse(data) : {};
      const userData = allData[userId];
      const localData = Array.isArray(userData) ? userData : (userData?.places || userData?.contacts || []);
      console.log('localStorage data:', localData.length);
      setLocalStorageData(localData);

      // Refresh server file display
      const userInfo = getUserInfo();
      if (userInfo?.peopleUuid && userInfo?.storageKey && loadFromFile) {
        console.log('Loading from server file...');
        const serverData = await loadFromFile(userInfo.peopleUuid, userInfo.storageKey);
        console.log('Server file response:', serverData?.length || 0);
        setServerFileData(serverData || []);
      } else {
        console.warn('Missing peopleUuid or storageKey - cannot load server file');
        setServerFileData([]);
      }
    } catch (error) {
      console.error('Error refreshing debug data:', error);
      addLog(`Error: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Force sync to server
  const forceSyncToServer = async () => {
    const userInfo = getUserInfo();
    if (!userInfo?.peopleUuid || !userInfo?.storageKey) {
      addLog('Missing peopleUuid or storageKey', 'error');
      return;
    }

    if (!saveToFile) {
      addLog('saveToFile function not provided', 'error');
      return;
    }

    setLoading(true);
    addLog('Syncing to server...');
    try {
      const result = await saveToFile(userInfo.peopleUuid, userInfo.storageKey, localStorageData);
      const success = result.success || result.SUCCESS;
      if (success) {
        addLog('Sync successful!', 'success');
        await refreshData();
        onDataChange?.();
      } else {
        addLog(`Sync failed: ${result.message || result.MESSAGE}`, 'error');
      }
    } catch (error) {
      addLog(`Sync error: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fix User Info - fetch from API
  const fixUserInfo = async () => {
    const userUuid = localStorage.getItem('userUuid');
    const authToken = localStorage.getItem('authToken');
    if (!userUuid) {
      addLog('ERROR: userUuid not found in localStorage', 'error');
      return;
    }
    if (!authToken) {
      addLog('ERROR: authToken not found in localStorage', 'error');
      return;
    }
    addLog(`Fetching user info for UUID: ${userUuid}...`);
    addLog(`Token: ${authToken.substring(0, 20)}...`);
    try {
      const response = await fetch(`https://apicabaza.creago.be/rest/cabaza/peoples/${userUuid}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      addLog(`Response status: ${response.status} ${response.statusText}`);

      const text = await response.text();
      addLog(`Response length: ${text.length} chars`);

      if (!text || text.length === 0) {
        addLog('ERROR: Empty response from API', 'error');
        return;
      }

      let result;
      try {
        result = JSON.parse(text);
      } catch (parseErr) {
        addLog(`ERROR: Invalid JSON - ${text.substring(0, 100)}`, 'error');
        return;
      }

      addLog(`API response: ${JSON.stringify(result).substring(0, 150)}...`);
      if (result.success || result.SUCCESS) {
        const data = result.data || result.DATA;
        const userObj = {
          id: userId,
          email: data.email || localStorage.getItem('userEmail'),
          firstName: data.firstname || data.firstName,
          lastName: data.lastname || data.lastName,
          peopleUuid: data.peopleuuid || data.peopleUuid || userUuid,
          storageKey: data.storagekey || data.storageKey,
          isAdmin: data.isadmin || data.isAdmin
        };
        localStorage.setItem('cabaza_user', JSON.stringify(userObj));
        addLog(`SUCCESS: cabaza_user created with storageKey: ${userObj.storageKey ? 'present' : 'MISSING'}`, userObj.storageKey ? 'success' : 'error');
        // Refresh data
        await refreshData();
      } else {
        addLog(`ERROR: ${result.message || result.MESSAGE}`, 'error');
      }
    } catch (err) {
      addLog(`ERROR: ${err.message}`, 'error');
    }
  };

  // Test Load - load from server and update localStorage
  const testLoad = async () => {
    const userInfo = getUserInfo();
    if (!userInfo?.peopleUuid || !userInfo?.storageKey) {
      addLog('ERROR: peopleuuid or storageKey missing', 'error');
      return;
    }

    addLog('Loading from server file...');
    try {
      const serverData = await loadFromFile(userInfo.peopleUuid, userInfo.storageKey);
      addLog(`Server response: ${JSON.stringify(serverData).substring(0, 200)}...`);
      if (serverData && serverData.length > 0) {
        addLog(`SUCCESS: Loaded ${serverData.length} items from server`, 'success');
        // Update localStorage and display
        const data = localStorage.getItem(localStorageKey);
        const allData = data ? JSON.parse(data) : {};
        allData[userId] = serverData;
        localStorage.setItem(localStorageKey, JSON.stringify(allData));
        setLocalStorageData(serverData);
        setMainData?.(serverData);
        addLog(`Updated localStorage and display with ${serverData.length} items`, 'success');
      } else {
        addLog('Server file empty or not found', 'error');
      }
    } catch (err) {
      addLog(`ERROR: ${err.message}`, 'error');
    }
  };

  // Full Resync - clear cache and reload from server or API
  const fullResync = async () => {
    const userInfo = getUserInfo();

    localStorage.removeItem(localStorageKey);
    setLoading(true);

    try {
      // First try to load from encrypted server file
      if (userInfo?.peopleUuid && userInfo?.storageKey) {
        addLog('Loading from encrypted server file...');
        const serverData = await loadFromFile(userInfo.peopleUuid, userInfo.storageKey);
        if (serverData && serverData.length > 0) {
          addLog(`Loaded ${serverData.length} items from server file`, 'success');
          // Update localStorage
          const allData = {};
          allData[userId] = serverData;
          localStorage.setItem(localStorageKey, JSON.stringify(allData));
          setLocalStorageData(serverData);
          setMainData?.(serverData);
          setLoading(false);
          return;
        }
        addLog('No server file found, triggering onDataChange...');
      }

      // Fallback - trigger parent reload
      onDataChange?.();
      await refreshData();
      addLog('Full resync complete', 'success');
    } catch (err) {
      addLog(`ERROR: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Open panel and refresh data
  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    if (newState) {
      refreshData();
    }
  };

  // Format cell value based on field config
  const formatCellValue = (item, field) => {
    const value = item[field.key];

    if (field.format === 'boolean') {
      if (value === true || value === 'true' || value === 1) {
        return <span className="text-green-400">{field.trueIcon || '✓'}</span>;
      }
      return <span className="text-gray-600">-</span>;
    }

    if (field.format === 'date' && value) {
      return new Date(value).toLocaleDateString();
    }

    if (field.format === 'truncate' && value) {
      const str = String(value);
      return str.length > 8 ? str.substring(0, 8) + '...' : str;
    }

    if (field.format === 'badge' && value) {
      const bgColor = value === 'PUBLIC' ? 'bg-green-900 text-green-300' : 'bg-gray-700 text-gray-300';
      return <span className={`px-1.5 py-0.5 rounded text-xs ${bgColor}`}>{value}</span>;
    }

    return value || '-';
  };

  // Don't render if not admin
  if (!isAdmin) return null;

  return (
    <>
      {/* Toggle Button - Fixed bottom right, above sidebar (z-[9999]) */}
      <div className="fixed bottom-4 right-4 z-[9999]">
        <button
          onClick={handleToggle}
          className="p-3 bg-gray-800 text-white rounded-full shadow-lg hover:bg-gray-700 transition-colors"
          title={title}
        >
          <Bug className="w-5 h-5" />
        </button>
      </div>

      {/* Full Screen Panel - above sidebar (z-[9999]) */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
          <div className="bg-gray-900 text-green-400 rounded-lg w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 bg-gray-800 border-b border-gray-700 flex justify-between items-center">
              <h3 className="font-mono text-white flex items-center gap-2">
                <Bug className="w-5 h-5" />
                {title}
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={refreshData}
                  disabled={loading}
                  className="text-xs bg-blue-700 text-white px-3 py-1.5 rounded hover:bg-blue-600 flex items-center gap-1 disabled:opacity-50"
                >
                  <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <button
                  onClick={forceSyncToServer}
                  disabled={loading}
                  className="text-xs bg-purple-700 text-white px-3 py-1.5 rounded hover:bg-purple-600 flex items-center gap-1 disabled:opacity-50"
                >
                  <UploadCloud className="w-3 h-3" />
                  Force Sync
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-xs bg-gray-600 text-white px-3 py-1.5 rounded hover:bg-gray-500"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="p-4 overflow-y-auto flex-1">
              {/* User Info */}
              <div className="mb-4 p-3 bg-gray-800 rounded">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-sm font-mono text-white">User Info</h4>
                  <button
                    onClick={fixUserInfo}
                    disabled={loading}
                    className="text-xs bg-yellow-600 text-white px-2 py-1 rounded hover:bg-yellow-500 disabled:opacity-50"
                  >
                    Fix User Info
                  </button>
                </div>
                {(() => {
                  const userInfo = getUserInfo();
                  const userJson = localStorage.getItem('cabaza_user');
                  const cabazaUser = userJson ? JSON.parse(userJson) : null;
                  return cabazaUser ? (
                    <div className="text-xs font-mono space-y-1">
                      <div><span className="text-gray-400">userId:</span> <span className="text-cyan-400">{userId}</span></div>
                      <div><span className="text-gray-400">peopleUuid:</span> <span className="text-purple-400">{userInfo?.peopleUuid || 'MISSING'}</span></div>
                      <div><span className="text-gray-400">storageKey:</span> <span className={userInfo?.storageKey ? 'text-green-400' : 'text-red-400'}>{userInfo?.storageKey ? userInfo.storageKey.substring(0, 16) + '...' : 'MISSING'}</span></div>
                    </div>
                  ) : (
                    <div className="text-xs font-mono space-y-1">
                      <p className="text-red-400">cabaza_user not found in localStorage</p>
                      <p className="text-gray-400">userUuid: {localStorage.getItem('userUuid') || 'MISSING'}</p>
                      <p className="text-yellow-400">Click "Fix User Info" to fetch from API</p>
                    </div>
                  );
                })()}
              </div>

              {/* Debug Logs */}
              {debugLogs.length > 0 && (
                <div className="mb-4 p-3 bg-gray-800 rounded max-h-32 overflow-y-auto">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-sm font-mono text-white">Debug Logs</h4>
                    <button onClick={() => setDebugLogs([])} className="text-xs text-gray-400 hover:text-white">Clear</button>
                  </div>
                  <div className="text-xs font-mono space-y-1">
                    {debugLogs.map((log, i) => (
                      <div key={i} className={log.type === 'error' ? 'text-red-400' : log.type === 'success' ? 'text-green-400' : 'text-gray-300'}>
                        [{log.time}] {log.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* localStorage Table */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-mono text-white">
                    localStorage[{userId}] - {localStorageData.length} items
                  </h4>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={testLoad}
                      disabled={loading}
                      className="text-xs bg-blue-700 text-white px-2 py-1 rounded hover:bg-blue-600 disabled:opacity-50"
                    >
                      Test Load
                    </button>
                    <button
                      onClick={forceSyncToServer}
                      disabled={loading}
                      className="text-xs bg-purple-700 text-white px-2 py-1 rounded hover:bg-purple-600 disabled:opacity-50"
                    >
                      Test Save
                    </button>
                    <button
                      onClick={fullResync}
                      disabled={loading}
                      className="text-xs bg-green-700 text-white px-2 py-1 rounded hover:bg-green-600 flex items-center gap-1 disabled:opacity-50"
                    >
                      <RefreshCw className="w-3 h-3" />
                      Full Resync
                    </button>
                  </div>
                </div>
                {localStorageData.length === 0 ? (
                  <p className="text-yellow-400 text-sm font-mono">Aucune donnée dans le localStorage</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs font-mono">
                      <thead>
                        <tr className="text-gray-400 border-b border-gray-700">
                          <th className="text-left p-2">#</th>
                          {fields.map(field => (
                            <th key={field.key} className="text-left p-2" style={{ maxWidth: field.width }}>
                              {field.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {localStorageData.map((item, index) => (
                          <tr key={item.localUuid || item.localId || item.id || index} className="border-b border-gray-800 hover:bg-gray-800">
                            <td className="p-2 text-gray-500">{index + 1}</td>
                            {fields.map(field => (
                              <td
                                key={field.key}
                                className={`p-2 ${field.color || 'text-gray-300'} ${field.width ? 'max-w-[' + field.width + '] truncate' : ''}`}
                                title={String(item[field.key] || '')}
                              >
                                {formatCellValue(item, field)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Server File Table */}
              <div className="mb-4 border-t border-gray-700 pt-4">
                <h4 className="text-sm font-mono text-orange-400 mb-3">
                  Server File ({serverFileName}) - {serverFileData.length} items
                </h4>
                {serverFileData.length === 0 ? (
                  <p className="text-yellow-400 text-sm font-mono">Aucune donnée dans le fichier serveur</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs font-mono">
                      <thead>
                        <tr className="text-orange-400 border-b border-gray-700">
                          <th className="text-left p-2">#</th>
                          {fields.map(field => (
                            <th key={field.key} className="text-left p-2" style={{ maxWidth: field.width }}>
                              {field.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {serverFileData.map((item, index) => (
                          <tr key={item.localUuid || item.localId || item.id || index} className="border-b border-gray-800 hover:bg-gray-800">
                            <td className="p-2 text-gray-500">{index + 1}</td>
                            {fields.map(field => (
                              <td
                                key={field.key}
                                className={`p-2 ${field.serverColor || field.color || 'text-gray-300'} ${field.width ? 'max-w-[' + field.width + '] truncate' : ''}`}
                                title={String(item[field.key] || '')}
                              >
                                {formatCellValue(item, field)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Raw JSON views */}
              <details className="mt-4">
                <summary className="text-gray-400 text-xs cursor-pointer hover:text-white flex items-center gap-1">
                  <ChevronDown className="w-3 h-3" />
                  Voir JSON brut localStorage
                </summary>
                <pre className="mt-2 text-xs text-gray-300 bg-gray-800 p-2 rounded overflow-x-auto max-h-60">
                  {JSON.stringify(localStorageData, null, 2)}
                </pre>
              </details>
              <details className="mt-2">
                <summary className="text-orange-400 text-xs cursor-pointer hover:text-white flex items-center gap-1">
                  <ChevronDown className="w-3 h-3" />
                  Voir JSON brut Server File
                </summary>
                <pre className="mt-2 text-xs text-orange-300 bg-gray-800 p-2 rounded overflow-x-auto max-h-60">
                  {JSON.stringify(serverFileData, null, 2)}
                </pre>
              </details>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DebugPanel;
